import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Slugs that have been superseded — client-side redirect (the server layer
// already answers these with a real HTTP 301 for crawlers).
const SLUG_REDIRECTS = {
  "quiet-luxury-true-luxury-unseen": "/perspectives/quiet-luxury",
  "what-does-one-of-one-really-mean": "/perspectives/what-does-one-of-one-mean",
  "psychology-of-owning-unique": "/perspectives/psychology-of-owning-something-unique",
};

// Perspective 0 is the Manifesto — it stands outside the numbered series.
const perspectiveLabel = (p) =>
  p.perspective_number === 0
    ? "THE MANIFESTO"
    : `PERSPECTIVE ${String(p.perspective_number).padStart(3, "0")}`;

const Block = ({ block }) => {
  if (block.type === "h2")
    return (
      <h2 className="pt-10 font-editorial font-light text-2xl md:text-3xl text-white">
        {block.text}
      </h2>
    );
  if (block.type === "h3")
    return (
      <h3 className="pt-6 font-mono-fmf text-base uppercase tracking-[0.25em] text-white">
        {block.text}
      </h3>
    );
  if (block.type === "quote")
    return (
      <blockquote className="py-10 font-editorial italic font-light text-2xl md:text-3xl leading-snug text-white text-center">
        {block.text}
      </blockquote>
    );
  if (block.type === "table")
    return (
      <figure className="py-14" data-testid="article-comparison">
        <table
          className={`${
            block.mobile_blocks ? "hidden md:table " : ""
          }w-full border-collapse text-left`}
        >
          <caption className="sr-only">
            {block.caption || "Editorial comparison"}
          </caption>
          <thead>
            <tr>
              {block.columns.map((c, i) => (
                <th
                  key={i}
                  scope="col"
                  className="pb-6 pr-4 md:pr-8 align-bottom font-mono-fmf font-normal text-[11px] md:text-[12px] uppercase tracking-[0.2em] md:tracking-[0.3em] text-[var(--dim)]"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => (
              <tr
                key={i}
                className="border-t"
                style={{ borderColor: "var(--line-soft)" }}
              >
                <th
                  scope="row"
                  className="py-5 pr-4 md:pr-8 align-top font-mono-fmf font-normal text-[11px] md:text-[12px] uppercase tracking-[0.2em] md:tracking-[0.25em] text-[var(--dim)]"
                >
                  {row[0]}
                </th>
                {row.slice(1).map((c, j) => (
                  <td
                    key={j}
                    className="py-5 pr-4 md:pr-8 align-top font-editorial font-light text-base md:text-xl text-white"
                  >
                    {c}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {block.mobile_blocks && (
          <div className="md:hidden space-y-14">
            {block.columns.slice(1).map((col, j) => (
              <section key={j}>
                <h3 className="font-mono-fmf text-[12px] uppercase tracking-[0.3em] text-white">
                  {col}
                </h3>
                <dl className="mt-2">
                  {block.rows.map((row, i) => (
                    <div
                      key={i}
                      className={i === 0 ? "pt-4" : "pt-5 mt-5 border-t"}
                      style={i === 0 ? undefined : { borderColor: "var(--line-soft)" }}
                    >
                      <dt className="font-mono-fmf text-[11px] uppercase tracking-[0.2em] text-[var(--dim)]">
                        {row[0]}
                      </dt>
                      <dd className="mt-1 font-editorial font-light text-base text-white">
                        {row[j + 1]}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            ))}
          </div>
        )}
      </figure>
    );
  if (block.type === "definitions")
    return (
      <section aria-label="Definitions" className="pt-16" data-testid="article-definitions">
        <h2 className="pt-10 font-editorial font-light text-2xl md:text-3xl text-white">
          Definitions
        </h2>
        {block.items.map((item, i) => (
          <div key={i} className="pt-10">
            <h3 className="font-mono-fmf text-base uppercase tracking-[0.25em] text-white">
              {item.q}
            </h3>
            <p className="mt-4 text-base md:text-lg font-light leading-loose text-white">
              {item.a}
            </p>
          </div>
        ))}
      </section>
    );
  if (block.type === "image")
    return (
      <figure className="py-8">
        <img
          src={block.src}
          alt={block.alt || ""}
          loading="lazy"
          decoding="async"
          width={block.width || undefined}
          height={block.height || undefined}
          className="w-full"
        />
        {block.caption && (
          <figcaption className="mt-4 text-center font-mono-fmf text-[12px] tracking-[0.25em] text-[var(--dim)]">
            {block.caption}
          </figcaption>
        )}
      </figure>
    );
  return (
    <p className="text-base md:text-lg font-light leading-loose text-white">{block.text}</p>
  );
};

export default function PerspectiveArticle() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [all, setAll] = useState([]);
  const [missing, setMissing] = useState(false);

  // Perspectives pages always sit on solid deep black — no carbon texture.
  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = "#070708";
    return () => {
      document.body.style.background = prev;
    };
  }, []);

  useEffect(() => {
    setP(null);
    setMissing(false);
    axios
      .get(`${API}/perspectives/${slug}`)
      .then((r) => setP(r.data))
      .catch(() => setMissing(true));
    axios
      .get(`${API}/perspectives`)
      .then((r) => setAll(r.data))
      .catch(() => {});
  }, [slug]);

  useSeo(
    p
      ? {
          title: p.meta_title || `${p.title} — FMF Perspectives`,
          description: p.meta_description || p.excerpt,
          path: `/perspectives/${p.slug}`,
          image: p.og_image || p.hero_image || undefined,
          type: "article",
          noindex: p.index_status === "noindex",
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "Article",
              headline: p.title,
              description: p.meta_description || p.excerpt,
              datePublished: p.publication_date,
              author: { "@type": "Organization", name: p.author || "FMF" },
              publisher: { "@type": "Organization", name: "FMF" },
              mainEntityOfPage: `${window.location.origin}/perspectives/${p.slug}`,
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "FMF", item: window.location.origin },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Perspectives",
                  item: `${window.location.origin}/perspectives`,
                },
                { "@type": "ListItem", position: 3, name: p.title },
              ],
            },
          ],
        }
      : { title: "Perspectives — FMF", description: "", path: `/perspectives/${slug}` }
  );

  if (SLUG_REDIRECTS[slug]) return <Navigate to={SLUG_REDIRECTS[slug]} replace />;
  if (slug === "noise-is-not-luxury")
    return <Navigate to="/noise-is-not-luxury" replace />;

  if (missing)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p data-testid="article-missing" className="font-mono-fmf text-sm text-[var(--dim)]">
          This Perspective does not exist.
        </p>
      </div>
    );
  if (!p)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="font-mono-fmf text-sm text-[var(--dim)]">…</p>
      </div>
    );

  const related = (p.related_articles || [])
    .map((s) => all.find((x) => x.slug === s))
    .filter(Boolean);

  return (
    <main data-testid="perspective-article" className="relative min-h-screen px-6 md:px-14 pt-40 pb-[14vh]" style={{ background: "#070708" }}>
      <Link
        data-testid="article-home-link"
        to="/"
        className="absolute top-8 left-6 md:left-14"
      >
        <img src="/fmf-logo-white.png" alt="FMF" className="w-14 opacity-90" />
      </Link>
      <article>
      <header className="max-w-2xl mx-auto text-center">
        <Reveal>
          <p
            data-testid="article-number"
            className="font-mono-fmf text-[13px] tracking-[0.5em] text-[var(--dim)]"
          >
            {perspectiveLabel(p)}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <h1
            data-testid="article-title"
            className="mt-10 font-editorial font-light text-4xl md:text-6xl leading-tight text-white"
          >
            {p.display_title
              ? p.display_title.split("\n").map((line, i) => (
                  <span key={i} className="block">
                    {line}
                  </span>
                ))
              : p.title}
          </h1>
        </Reveal>
        {p.subtitle && (
          <Reveal delay={0.3}>
            <p
              data-testid="article-subtitle"
              className="mt-8 font-editorial italic text-xl md:text-2xl text-[var(--bone-dim)]"
            >
              {p.subtitle}
            </p>
          </Reveal>
        )}
        <Reveal delay={0.45}>
          <p
            data-testid="article-meta"
            className="mt-10 font-mono-fmf text-[12px] uppercase tracking-[0.3em] text-[var(--dim)]"
          >
            {p.category} · {p.publication_date} · {p.reading_time} · {p.author}
          </p>
        </Reveal>
      </header>

      {p.hero_image && (
        <Reveal delay={0.3}>
          <img
            data-testid="article-hero-image"
            src={p.hero_image}
            alt={p.title}
            className="mt-[8vh] w-full max-w-4xl mx-auto"
          />
        </Reveal>
      )}

      <div className="mt-[10vh] max-w-xl mx-auto space-y-8">
        {p.intro && (
          <Reveal>
            <p
              data-testid="article-intro"
              className="font-editorial font-light text-xl md:text-2xl leading-relaxed text-white"
            >
              {p.intro}
            </p>
          </Reveal>
        )}
        {p.body && p.body.length > 0 ? (
          p.body.map((b, i) => (
            <Reveal key={i}>
              <Block block={b} />
            </Reveal>
          ))
        ) : (
          <p
            data-testid="article-forthcoming"
            className="text-center font-mono-fmf text-[13px] tracking-[0.3em] uppercase text-[var(--dim)]"
          >
            This Perspective is being written.
          </p>
        )}
      </div>

      <div className="mt-[12vh] max-w-xl mx-auto text-center space-y-6">
        {p.torque_note && (
          <Reveal>
            <p data-testid="article-torque-note" className="font-mono-fmf text-[13px] tracking-[0.25em] text-[var(--bone-dim)]">
              <Link to="/" className="hover:text-white transition-colors duration-700">
                TORQUE 1/1
              </Link>{" "}
              is our interpretation of this idea.
            </p>
          </Reveal>
        )}
        {p.high_intent && (
          <Reveal>
            <Link
              data-testid="article-private-conversation"
              to="/#dialogue"
              className="inline-block font-mono-fmf text-[13px] uppercase tracking-[0.35em] text-white border-b border-transparent hover:border-white transition-colors duration-700 pb-1"
            >
              Request a Private Conversation
            </Link>
          </Reveal>
        )}
        {p.closing && p.closing.lines && (
          <Reveal>
            <p
              data-testid="article-closing"
              className="font-editorial font-light text-xl md:text-2xl leading-relaxed text-white"
            >
              {p.closing.lines.map((line, i) => (
                <span key={i} className="block">
                  {line}
                </span>
              ))}
            </p>
          </Reveal>
        )}
        <p className="font-mono-fmf text-[12px] uppercase tracking-[0.4em] text-[var(--dim)]">
          A Perspective by FMF.
        </p>
        {p.closing && p.closing.link_href && (
          <Reveal>
            <Link
              data-testid="article-closing-link"
              to={p.closing.link_href}
              className="inline-block font-mono-fmf text-[13px] tracking-[0.25em] text-[var(--dim)] hover:text-white transition-colors duration-700"
            >
              {p.closing.link_text}
            </Link>
          </Reveal>
        )}
      </div>

      {related.length > 0 && (
        <div className="mt-[12vh] max-w-2xl mx-auto border-t pt-14" style={{ borderColor: "var(--line-soft)" }}>
          <p className="text-center font-mono-fmf text-[12px] uppercase tracking-[0.4em] text-[var(--dim)]">
            Related Perspectives
          </p>
          <div className="mt-10 space-y-10">
            {related.map((r) => (
              <Link
                key={r.slug}
                data-testid={`related-${r.perspective_number}`}
                to={r.slug === "noise-is-not-luxury" ? "/noise-is-not-luxury" : `/perspectives/${r.slug}`}
                className="group block text-center"
              >
                <span className="font-mono-fmf text-[11px] tracking-[0.5em] text-[var(--dim)]">
                  {perspectiveLabel(r)}
                </span>
                <span className="block mt-3 font-editorial font-light text-2xl md:text-3xl text-white group-hover:opacity-60 transition-opacity duration-700">
                  {r.title}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-20 text-center">
        <Link
          data-testid="article-back-index"
          to="/perspectives"
          className="font-mono-fmf text-[12px] uppercase tracking-[0.4em] text-[var(--dim)] hover:text-white transition-colors duration-700"
        >
          All Perspectives
        </Link>
      </div>
      </article>
    </main>
  );
}
